// src/Pages/Landing/TrubaduuriUusimaaLanding.jsx
import React, { useMemo } from "react";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import PriceInfoBox from "../../components/PriceInfoBox/PriceInfoBox.jsx";
import { trackLandingCta } from "../../utils/analytics";

/**
 * Trubaduuri Uusimaa -landing React Shelliin.
 * Metat/JSON-LD tulevat WordPressistä.
 * Tavoite: selkeä hinnoittelu – trubaduurien keskihinnat Uudellamaalla.
 */

const UUSIMAA_SLUG = "uusimaa";

export default function TrubaduuriUusimaaLanding() {
  // Paikallisen päivän ISO (YYYY-MM-DD)
  const today = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  return (
    <section className="relative max-w-6xl mx-auto px-4 pt-24 pb-24">
      {/* Hero / ingressi */}
      <header className="rounded-2xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur-md p-8 md:p-12 shadow-lg">
        <h1 className="text-4xl md:text-6xl font-bold leading-tight text-black dark:text-white">
          Trubaduuri Uudellamaalla – vapaat esiintyjät
        </h1>

        <p className="mt-4 text-black/80 dark:text-white/80 text-base md:text-lg max-w-3xl">
          Buukkerista löydät Uudenmaan vapaat trubaduurit reaaliaikaisesti.{" "}
          <strong>
            Valitse päivämäärä, katso vapaat esiintyjät ja pyydä tarjous parilla
            klikkauksella.
          </strong>
        </p>

        <p className="mt-3 text-black/80 dark:text-white/80 text-base md:text-lg max-w-3xl">
          Tarjottu hinta on <strong>kokonaishinta (sis. matkakulut)</strong>.
          Lisäksi esiintyjältä voi tilata lisäpalveluja tarpeen mukaan – esimerkiksi
          taustamusiikkia illan alkuun ja loppuun, pientä juonnettua ohjelmaa tai
          äänentoiston tuonnin paikan päälle.
        </p>

        {/* CTA-napit */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <a
            href={`/?type=trubaduuri&maakunta=uusimaa&date=${today}&clear=1#tulokset`}
            aria-label="Näytä tämän päivän vapaat (Uusimaa)"
            onClick={() =>
              trackLandingCta({
                actType: "trubaduuri",
                region: "uusimaa",
                placement: "hero_primary_today",
              })
            }
          >
            <NeonButton variant="primary" size="md" className="font-semibold">
              Näytä tämän päivän vapaat (Uusimaa)
            </NeonButton>
          </a>

          <a
            href="/?type=trubaduuri&book=1&maakunta=uusimaa&clear=1#tulokset"
            aria-label="Avaa pikahaku"
            onClick={() =>
              trackLandingCta({
                actType: "trubaduuri",
                region: "uusimaa",
                placement: "hero_quick_search",
              })
            }
          >
            <NeonButton variant="secondary" size="md" className="font-semibold">
              Avaa pikahaku
            </NeonButton>
          </a>
        </div>
      </header>

      {/* Hintainfoboksi: trubaduurien keskihinnat Uudellamaalla */}
      <div className="mt-8">
        <PriceInfoBox
          title="Trubaduurien keskihinnat Uudellamaalla – suosituimmat setit"
          types={["trubaduuri"]}
          autoTopSets
          topN={3}
          regionSlug={UUSIMAA_SLUG}   // ← käytetään Uudenmaan mediaaneja/keskiarvoja
          refreshMs={15 * 60 * 1000}
          compact={true}
          nationalLabel="Buukkerin keskihinta"
        />
        <p className="mt-2 text-black/80 dark:text-white/80 text-sm opacity-70">
          Hinta on suuntaa-antava keskiarvo Uudeltamaalta.{" "}
          <strong>Lopullinen hinta täsmentyy tarjouspyynnössä</strong> ja se
          sisältää myös matkakulut. Hinnat perustuvat Buukkerin trubaduurien
          hinnastoihin, joissa{" "}
          <strong>
            Uusimaa on merkitty keikkailualueeksi tai esiintyjän kotipaikaksi
          </strong>{" "}
          – ei pelkästään yksittäiseen kaupungin nimeen.
        </p>
      </div>

      {/* Ristiinlinkitys */}
      <div className="mt-8 rounded-2xl p-6 md:p-8 bg-white/70 dark:bg-zinc-900/60 ring-1 ring-black/10 dark:ring-white/10 shadow-lg">
        <p className="text-black/80 dark:text-white/80 text-base md:text-lg">
          Tarvitsetko bilebändiä tai DJ:tä Uudellamaalla? Siirry{" "}
          <a className="underline" href="/bilebandi-uusimaa/">
            Bilebändi Uusimaa
          </a>{" "}
          tai{" "}
          <a className="underline" href="/dj-uusimaa/">
            DJ Uusimaa
          </a>{" "}
          -sivulle. Lue myös:{" "}
          <a
            className="underline"
            href="https://buukkeri.fi/blogi/nain-loydat-taydellisen-esiintyjan-juhliisi"
          >
            Näin löydät täydellisen esiintyjän juhliisi
          </a>
          .
        </p>
      </div>
    </section>
  );
}
